import React, { useCallback, useEffect, useState } from "react";
import { Pencil, Trash2, Plus } from "lucide-react";
import { brl } from "../lib";

const VAZIO = { descricao: "", quantidade: "", unidade: "m²", preco_unitario: "", fornecedor: "", material_id: "" };
const UNIDADES = ["m²", "m", "peça", "unidade", "kg", "chapa"];
const num = (v) => Number(String(v ?? "").replace(",", ".")) || 0;

function ItemForm({ inicial, materiais, onSave, onCancel, testid }) {
  const [f, setF] = useState({ ...VAZIO, ...inicial });
  const [salvando, setSalvando] = useState(false);
  const set = (k) => (e) => setF(p => ({ ...p, [k]: e.target.value }));
  const escolherMaterial = (e) => {
    const m = materiais.find(x => x.id === e.target.value);
    setF(p => ({ ...p, material_id: e.target.value, descricao: p.descricao || m?.nome || "", fornecedor: p.fornecedor || m?.fornecedor || "" }));
  };
  const salvar = async () => {
    if (!f.descricao.trim()) return alert("Informe a descrição.");
    setSalvando(true);
    try { await onSave({ ...f, quantidade: num(f.quantidade), preco_unitario: num(f.preco_unitario) }); } finally { setSalvando(false); }
  };
  return (
    <div className="form-grid" style={{ padding: 16, background: "var(--bg-3)", border: "1px solid var(--line)", marginBottom: 12 }} data-testid={testid}>
      {materiais.length > 0 && (
        <div className="field span2"><label>Material do catálogo (opcional)</label>
          <select value={f.material_id || ""} onChange={escolherMaterial} data-testid={`${testid}-material`}>
            <option value="">— nenhum —</option>
            {materiais.map(m => <option key={m.id} value={m.id}>{m.nome}</option>)}
          </select>
        </div>
      )}
      <div className="field span2"><label>Descrição *</label><input value={f.descricao} onChange={set("descricao")} data-testid={`${testid}-descricao`} /></div>
      <div className="field"><label>Quantidade</label><input type="number" step="0.01" value={f.quantidade} onChange={set("quantidade")} data-testid={`${testid}-quantidade`} /></div>
      <div className="field"><label>Unidade</label>
        <select value={f.unidade || ""} onChange={set("unidade")} data-testid={`${testid}-unidade`}>
          {UNIDADES.map(u => <option key={u} value={u}>{u}</option>)}
        </select>
      </div>
      <div className="field"><label>Preço unitário (R$)</label><input type="number" step="0.01" value={f.preco_unitario} onChange={set("preco_unitario")} data-testid={`${testid}-preco`} /></div>
      <div className="field"><label>Fornecedor</label><input value={f.fornecedor || ""} onChange={set("fornecedor")} data-testid={`${testid}-fornecedor`} /></div>
      <div className="span2" style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <button className="btn btn-ghost btn-sm" onClick={onCancel} data-testid={`${testid}-cancelar`}>Cancelar</button>
        <button className="btn btn-primary btn-sm" onClick={salvar} disabled={salvando} data-testid={`${testid}-salvar`}>{salvando ? "Salvando…" : "Salvar"}</button>
      </div>
    </div>
  );
}

/* Itens de material lançados na obra com quantidade, preço e fornecedor próprios. */
export default function ObraMateriaisItens({ obraId, materiais, api, canEdit }) {
  const [data, setData] = useState({ itens: [], total: 0 });
  const [editId, setEditId] = useState(null);
  const url = `/obras/${obraId}/materiais-itens`;
  const load = useCallback(() => api("get", url).then(setData).catch(() => { }), [api, url]);
  useEffect(() => { load(); }, [load]);
  const run = async (fn) => {
    try { await fn(); setEditId(null); await load(); } catch (e) { alert("Erro: " + (e?.response?.data?.detail || e.message)); }
  };
  const remover = (it) => window.confirm(`Excluir "${it.descricao}"?`) && run(() => api("delete", `${url}/${it.id}`));
  return (
    <div className="panel" style={{ marginTop: 32 }} data-testid="obra-materiais-itens">
      <div className="panel-head">
        <div><div className="panel-eyebrow">Quantidades e custos</div><h3 className="panel-title">Itens lançados <em>na obra</em></h3></div>
        {canEdit && editId !== "novo" && <button className="btn btn-sm" onClick={() => setEditId("novo")} data-testid="mat-item-novo"><Plus size={12} /> Lançar item</button>}
      </div>
      {editId === "novo" && <ItemForm materiais={materiais} onSave={b => run(() => api("post", url, b))} onCancel={() => setEditId(null)} testid="mat-item-form" />}
      {data.itens.length === 0 ? <p style={{ color: "var(--ink-3)", fontSize: 13 }}>Nenhum item lançado.</p> : (
        <div className="tbl-wrap" style={{ border: 0 }}>
          <table className="tbl">
            <thead><tr><th>Descrição</th><th style={{ textAlign: "right" }}>Qtd</th><th>Unid.</th><th style={{ textAlign: "right" }}>Preço unit.</th><th>Fornecedor</th><th style={{ textAlign: "right" }}>Total</th>{canEdit && <th></th>}</tr></thead>
            <tbody>
              {data.itens.map(it => editId === it.id ? (
                <tr key={it.id}><td colSpan={7}><ItemForm inicial={it} materiais={materiais} onSave={b => run(() => api("patch", `${url}/${it.id}`, b))} onCancel={() => setEditId(null)} testid={`mat-item-edit-${it.id}`} /></td></tr>
              ) : (
                <tr key={it.id} data-testid={`mat-item-${it.id}`}>
                  <td style={{ color: "var(--ink)" }}>{it.descricao}</td>
                  <td style={{ textAlign: "right" }}>{Number(it.quantidade || 0).toLocaleString("pt-BR")}</td>
                  <td>{it.unidade || "—"}</td>
                  <td style={{ textAlign: "right" }}>{brl(it.preco_unitario || 0)}</td>
                  <td>{it.fornecedor || "—"}</td>
                  <td style={{ textAlign: "right", color: "var(--gold)" }} data-testid={`mat-item-total-${it.id}`}>{brl((it.quantidade || 0) * (it.preco_unitario || 0))}</td>
                  {canEdit && <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                    <button className="btn btn-ghost btn-sm" onClick={() => setEditId(it.id)} title="Editar" data-testid={`mat-item-edit-btn-${it.id}`}><Pencil size={11} /></button>
                    <button className="btn btn-ghost btn-sm" onClick={() => remover(it)} style={{ marginLeft: 6, color: "#c47a7a" }} title="Excluir" data-testid={`mat-item-del-${it.id}`}><Trash2 size={11} /></button>
                  </td>}
                </tr>
              ))}
            </tbody>
            <tfoot><tr><td colSpan={5} style={{ textAlign: "right", color: "var(--ink-3)" }}>Total dos itens</td><td style={{ textAlign: "right", color: "var(--gold)", fontFamily: "'Fraunces',serif", fontSize: 16 }} data-testid="mat-itens-total">{brl(data.total)}</td>{canEdit && <td />}</tr></tfoot>
          </table>
        </div>
      )}
    </div>
  );
}
